import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import {
  CHANNEL_LABEL_KEY,
  DELIVERY_LABEL_KEY,
  DELIVERY_TONE,
} from '@/lib/notifications/labels';
import type { INotificationSendRow } from '@/lib/notifications/types';
import { notificationFailureReason } from './pnr-steps';

interface NotificationSendHistoryProps {
  sends: INotificationSendRow[];
}

export function NotificationSendHistory({ sends }: NotificationSendHistoryProps) {
  const { t } = useTranslation('pnr');

  if (sends.length === 0) return null;

  return (
    <div className="mt-2 w-full rounded-md border border-border bg-muted/30 p-2" data-testid="notification-send-history">
      <div className="text-xs font-medium text-muted-foreground mb-1.5">
        {t('notification.historyTitle')}
      </div>
      <ul className="space-y-1.5" data-testid="notification-history-list">
        {sends.map((send, idx) => (
          <li
            key={send.notificationUrn}
            className="flex items-center gap-2 text-xs"
            data-testid={`notification-history-item-${idx}`}
          >
            <Badge
              variant={DELIVERY_TONE[send.deliveryState]}
              size="sm"
              data-testid={`history-delivery-${idx}`}
            >
              {t(DELIVERY_LABEL_KEY[send.deliveryState])}
            </Badge>
            <span className="text-muted-foreground">
              {t(CHANNEL_LABEL_KEY[send.channel])}
            </span>
            <span className="text-muted-foreground">
              {formatTimestamp(send.lastEventAt)}
            </span>
            {send.failureReason ? (
              // The same words as the current-status line. An unknown code or free
              // provider text reads as `dispatch_failed`, never verbatim.
              <span className="text-destructive" data-testid={`history-failure-${idx}`}>
                ({t(`notification.current.failure.${notificationFailureReason(send.failureReason)}`)})
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatTimestamp(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
